import { useCallback, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import { useAppState } from "@/app";
import { GnbMenu } from "@/components/base";
import {
  PageBase,
  type PageBaseContext,
  type PageBaseProps,
  type PageBaseTabs,
} from "@/page/base/PageBase";
import { GpuQuotaRequestPanel } from "./GpuQuotaRequestPanel";
import { GpuQuotaTablePanel } from "./GpuQuotaTablePanel";

// GPU Quota. 두 표를 인페이지 탭으로 나눈다. 쿼터 탭은 workspace 현황(한도, 점유, 사용량)이다.
// 요청 탭은 조직의 한도 상향 요청 이력이다. 두 표는 컬럼 성격이 다르다. 운영자는 대기열만 보고
// 싶을 때가 많다. 그래서 별도 화면으로 둔다.
//
// 조직 멤버는 조회에서 200 을 받는다. 그래서 등급 게이트를 두지 않는다. GPU 는 공용 자원이고,
// 누가 무엇을 왜 요청했는지가 팀 사이 조율의 입력이다. 접수·종결 권한은 서버가 판정하고
// 응답의 can_* 로 보낸다.
//
// 요청은 기간이 있는 임시 증액이다. admin 이 요청 탭에서 승인하면 서버가 training-infra 에 증액을
// 반영한다. 기준 한도와 스케줄은 이 화면에서 바꾸지 않는다. 그 계약은 backend spec 이 소유한다.

const tabSearchParamKey = "tab";

const gpuQuotaTabValues = ["quota", "requests"] as const;
type GpuQuotaTab = (typeof gpuQuotaTabValues)[number];
const defaultGpuQuotaTab: GpuQuotaTab = "quota";

const gpuQuotaTabItems: PageBaseTabs["items"] = [
  { value: "quota", label: "Quota" },
  { value: "requests", label: "한도 요청" },
];

function normalizeTab(value: string | null): GpuQuotaTab {
  return gpuQuotaTabValues.includes(value as GpuQuotaTab)
    ? (value as GpuQuotaTab)
    : defaultGpuQuotaTab;
}

export function GpuQuotaPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = normalizeTab(searchParams.get(tabSearchParamKey));

  // 기본 탭이면 키를 지운다. 그래서 짧은 URL 이 canonical 이 된다.
  const handleTabChange = useCallback(
    (next: string) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        if (next === defaultGpuQuotaTab) {
          params.delete(tabSearchParamKey);
        } else {
          params.set(tabSearchParamKey, next);
        }
        return params;
      });
    },
    [setSearchParams],
  );

  const { state: appState } = useAppState();

  return (
    <GpuQuotaPageView
      pageTitle="GPU Quota"
      currentMenu={GnbMenu.GpuQuota}
      // 표가 본문을 채우고 내부에서 스크롤한다. 쿼터 탭은 작은 창에서 요약과 표를 함께 스크롤한다.
      // admin 은 선점 없이도 접수한다.
      canRequestAnyWorkspace={appState.currentUser?.role === "admin"}
      myMemberUuid={appState.currentUser?.memberUuid ?? null}
      tabs={{
        items: gpuQuotaTabItems,
        value: tab,
        onValueChange: handleTabChange,
        ariaLabel: "GPU Quota 탭",
      }}
    />
  );
}

type GpuQuotaPageViewProps = PageBaseProps & {
  canRequestAnyWorkspace: boolean;
  myMemberUuid: string | null;
};

class GpuQuotaPageView extends PageBase<GpuQuotaPageViewProps> {
  // 각 탭 패널이 자기 소스를 소유한다(적재와 정리 포함). 페이지는 얇은 셸이다. 탭을 바꿀 때마다
  // 다시 읽는 비용은 허용한다. 쿼터와 요청 이력은 초 단위로 바뀌지 않으므로 폴링을 두지 않는다.
  protected override renderContent(context: PageBaseContext): ReactNode {
    if (context.currentTab === "requests") {
      return <GpuQuotaRequestPanel myMemberUuid={this.props.myMemberUuid} />;
    }
    return <GpuQuotaTablePanel canRequestAnyWorkspace={this.props.canRequestAnyWorkspace} />;
  }
}
